import { useState } from 'react';
import { toast } from 'sonner';
import { MagnifyingGlass, Info } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import NasFileList from './NasFileList';
import NasFilePreview from './NasFilePreview';
import { nasApi } from './nasApi';
import { NAS_I18N } from './nasI18n';

// Search tab — queries nas_search_index ONLY (filename / path / metadata for
// this phase). Never touches the DMS search or the Document Assistant.
export default function NasArchiveSearch({ meta, roots }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const canDownload = !!meta?.flags?.nas_download;

  const run = async () => {
    const text = q.trim();
    if (!text) { toast.error('検索語を入力してください'); return; }
    setLoading(true); setDisabled(false);
    try {
      setRes(await nasApi.search(text));
    } catch (e) {
      if (e?.response?.status === 404) setDisabled(true);
      else toast.error(e?.response?.data?.detail || '検索に失敗しました');
      setRes(null);
    } finally { setLoading(false); }
  };

  if (disabled) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-6 text-center" data-testid="nas-search-disabled">
        <Info size={22} className="mx-auto text-zinc-400 mb-2" />
        <p className="text-sm font-medium text-zinc-700">{NAS_I18N.disabled_title}</p>
        <p className="text-sm text-zinc-500 mt-1">{NAS_I18N.disabled_hint}</p>
      </div>
    );
  }

  return (
    <div className="max-w-[960px]" data-testid="nas-search">
      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') run(); }}
          placeholder="ファイル名・NASパスで探す（この段階では意味検索は無効です）"
          className="h-12 pl-10 pr-28 text-base rounded-xl border-transparent bg-white ring-1 ring-zinc-200 focus-visible:ring-2 focus-visible:ring-teal-600"
          data-testid="nas-search-input" />
        <Button onClick={run} disabled={loading}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-9 bg-teal-700 hover:bg-teal-800 text-white"
          data-testid="nas-search-btn">{loading ? '検索中…' : '検索'}</Button>
      </div>
      <p className="text-xs text-zinc-400 mt-2 inline-flex items-center gap-1">
        <Info size={12} /> 意味検索（AI）は、権限確認済みのパイロット文書でベクトルを生成してから有効化されます。現在は無効です。
      </p>

      {res && (
        <div className="mt-5" data-testid="nas-search-results">
          {res.hits.length === 0 ? (
            <p className="text-sm text-zinc-400 py-10 text-center">見つかりませんでした</p>
          ) : (
            <>
              <p className="text-sm text-zinc-500 mb-2">{res.hits.length}件が見つかりました</p>
              <NasFileList files={res.hits} showLocation
                onOpen={(f) => setPreview(f.node_id)} />
            </>
          )}
        </div>
      )}
      {preview && meta?.flags?.nas_preview && (
        <NasFilePreview nodeId={preview} canDownload={canDownload} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}
